if (!customElements.get('bst-product-nav')) {
  customElements.define(
    'bst-product-nav',
    class BstProductNav extends HTMLElement {
      constructor() {
        super();
        this.links = [];
        this.sections = [];
        this.placeholder = null;
        this.stickStart = 0;
        this.isStickyEnabled = false;
        this.onLinkClick = this.onLinkClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.headerResizeObserver = null;
        this.activeAnchor = null;
        this.clickedAnchor = null;
        this.clickReleaseTimer = null;
      }

      connectedCallback() {
        this.links = Array.from(this.querySelectorAll('[data-bst-product-nav-link]'));
        this.filterMissingNavItems();

        if (!this.links.length) {
          this.hideNav();
          return;
        }

        this.isStickyEnabled = this.classList.contains('is-sticky');
        this.refreshSectionTargets();

        if (this.isStickyEnabled) {
          this.placeholder = document.createElement('div');
          this.placeholder.className = 'bst-product-nav__placeholder';
          this.placeholder.setAttribute('aria-hidden', 'true');
          this.parentNode?.insertBefore(this.placeholder, this);
          this.observeHeaderHeight();
        }

        this.updateStickyOffset();
        this.measureStickStart();
        this.updateFixedState();
        this.setActiveFromScroll();

        this.links.forEach((link) => {
          link.addEventListener('click', this.onLinkClick);
        });

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });

        requestAnimationFrame(() => {
          this.filterMissingNavItems();

          if (!this.links.length) {
            this.hideNav();
            return;
          }

          this.refreshSectionTargets();
          this.updateStickyOffset();
          this.measureStickStart();
          this.updateFixedState();
          this.setActiveFromScroll();
        });
      }

      filterMissingNavItems() {
        const links = Array.from(this.querySelectorAll('[data-bst-product-nav-link]'));
        const validLinks = [];

        links.forEach((link) => {
          const anchor = link.dataset.bstProductNavLink;
          const target = this.resolveNavTarget(anchor);

          if (!target) {
            link.closest('.bst-product-nav__item')?.remove();
            return;
          }

          validLinks.push(link);
        });

        this.links = validLinks;
        this.ensureFirstLinkActive();
      }

      ensureFirstLinkActive() {
        if (!this.links.length || this.activeAnchor) return;

        const firstAnchor = this.links[0].dataset.bstProductNavLink;
        this.setActiveLink(firstAnchor, { force: true });
      }

      hideNav() {
        this.links.forEach((link) => {
          link.removeEventListener('click', this.onLinkClick);
        });
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        this.clearClickedAnchor();
        this.headerResizeObserver?.disconnect();
        this.headerResizeObserver = null;
        this.placeholder?.remove();
        this.placeholder = null;
        this.classList.remove('is-fixed');

        this.closest('.shopify-section')?.classList.add('hidden');
        this.hidden = true;
      }

      resolveNavTarget(anchor) {
        if (!anchor) return null;

        const byId = document.getElementById(anchor);
        if (byId) return byId;

        const byData = document.querySelector(`[data-bst-anchor="${anchor}"]`);
        if (byData) return byData;

        if (anchor === 'reviews') {
          return (
            document.querySelector('.bst-product-reviews') ||
            document.querySelector('#stamped-main-widget') ||
            document.querySelector('#stamped-reviews-widget') ||
            document.querySelector('.stamped-main-widget') ||
            document.querySelector('.shopify-app-block[id*="stamped"]')?.closest('section, .bst-product-reviews')
          );
        }

        return null;
      }

      refreshSectionTargets() {
        this.sections = this.links
          .map((link) => this.resolveNavTarget(link.dataset.bstProductNavLink))
          .filter(Boolean);
      }

      disconnectedCallback() {
        this.links.forEach((link) => {
          link.removeEventListener('click', this.onLinkClick);
        });
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        this.clearClickedAnchor();
        this.headerResizeObserver?.disconnect();
        this.placeholder?.remove();
        this.placeholder = null;
      }

      observeHeaderHeight() {
        if (!('ResizeObserver' in window)) return;

        const observeTargets = [
          document.querySelector('.bst-header'),
          document.querySelector('.bst-header__bar'),
        ].filter(Boolean);

        if (!observeTargets.length) return;

        this.headerResizeObserver = new ResizeObserver(() => {
          this.updateStickyOffset();
          if (!this.classList.contains('is-fixed')) {
            this.measureStickStart();
          }
          this.updateFixedState();
        });

        observeTargets.forEach((target) => this.headerResizeObserver.observe(target));
      }

      onResize() {
        this.updateStickyOffset();
        this.refreshSectionTargets();
        this.measureStickStart();
        this.updateFixedState();
        this.setActiveFromScroll();
      }

      onScroll() {
        window.requestAnimationFrame(() => {
          if (this.classList.contains('is-fixed')) {
            this.updateStickyOffset();
          }
          this.updateFixedState();
          this.setActiveFromScroll();
        });
      }

      onLinkClick(event) {
        event.preventDefault();

        const link = event.currentTarget;
        const anchor = link.dataset.bstProductNavLink;
        if (!anchor) return;

        const target = this.resolveNavTarget(anchor);
        if (!target) return;

        this.lockClickedAnchor(anchor);
        this.setActiveLink(anchor, { scrollIntoView: true, force: true });
        this.scrollToTarget(target);
      }

      lockClickedAnchor(anchor) {
        this.clickedAnchor = anchor;
        window.clearTimeout(this.clickReleaseTimer);
        this.clickReleaseTimer = window.setTimeout(() => this.releaseClickedAnchor(), 1200);
      }

      releaseClickedAnchor() {
        if (!this.clickedAnchor) return;

        const anchor = this.clickedAnchor;
        this.clickedAnchor = null;
        window.clearTimeout(this.clickReleaseTimer);
        this.clickReleaseTimer = null;
        this.setActiveLink(anchor, { force: true });
      }

      clearClickedAnchor() {
        this.clickedAnchor = null;
        window.clearTimeout(this.clickReleaseTimer);
        this.clickReleaseTimer = null;
      }

      scrollToTarget(target) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

        this.updateStickyOffset();

        target.scrollIntoView({
          behavior,
          block: 'start',
        });

        const finish = () => {
          this.updateStickyOffset();
          this.updateFixedState();
          this.releaseClickedAnchor();
        };

        if (behavior === 'smooth') {
          if ('onscrollend' in window) {
            window.addEventListener('scrollend', finish, { once: true });
          }
          window.setTimeout(finish, 900);
          return;
        }

        window.requestAnimationFrame(finish);
      }

      getSectionAnchor(section) {
        return section.id || section.dataset.bstAnchor || null;
      }

      getActiveDetectionOffset() {
        return this.getAnchorScrollOffset();
      }

      setActiveFromScroll() {
        if (!this.sections.length || this.clickedAnchor) return;

        const offset = this.getActiveDetectionOffset();
        let bestAnchor = null;
        let bestVisible = 0;

        this.sections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          const visibleTop = Math.max(rect.top, offset);
          const visibleBottom = Math.min(rect.bottom, window.innerHeight);
          const visible = Math.max(0, visibleBottom - visibleTop);

          if (visible > bestVisible) {
            bestVisible = visible;
            bestAnchor = this.getSectionAnchor(section);
          }
        });

        if (!bestAnchor) {
          for (const section of this.sections) {
            const rect = section.getBoundingClientRect();
            if (rect.top <= offset) {
              bestAnchor = this.getSectionAnchor(section);
            }
          }
        }

        if (bestAnchor) {
          this.setActiveLink(bestAnchor);
        }
      }

      setActiveLink(anchor, options = {}) {
        const { scrollIntoView = false, force = false } = options;
        if (!force && this.activeAnchor === anchor) return;

        this.activeAnchor = anchor;

        this.links.forEach((link) => {
          const isActive = link.dataset.bstProductNavLink === anchor;
          link.classList.toggle('is-active', isActive);
          link.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        const activeLink = this.links.find((link) => link.dataset.bstProductNavLink === anchor);
        if (activeLink) {
          this.scrollLinkIntoView(activeLink, scrollIntoView);
        }
      }

      measureStickStart() {
        if (!this.isStickyEnabled || this.classList.contains('is-fixed')) return;
        this.stickStart = this.getBoundingClientRect().top + window.scrollY;
      }

      updateFixedState() {
        if (!this.isStickyEnabled || !this.placeholder) return;

        const headerOffset = this.getScrollOffset();
        const shouldFix = window.scrollY >= this.stickStart - headerOffset;

        if (shouldFix === this.classList.contains('is-fixed')) {
          if (shouldFix) {
            this.placeholder.style.height = `${this.offsetHeight}px`;
          }
          return;
        }

        this.classList.toggle('is-fixed', shouldFix);
        this.placeholder.style.height = shouldFix ? `${this.offsetHeight}px` : '0';

        if (!shouldFix) {
          requestAnimationFrame(() => this.measureStickStart());
        }
      }

      scrollLinkIntoView(link, smooth = true) {
        const list = link.closest('.bst-product-nav__list');
        if (!list || list.scrollWidth <= list.clientWidth) return;

        const linkLeft = link.offsetLeft;
        const linkWidth = link.offsetWidth;
        const linkRight = linkLeft + linkWidth;
        const scrollLeft = list.scrollLeft;
        const listWidth = list.clientWidth;
        const edgeInset = 8;

        if (linkLeft >= scrollLeft + edgeInset && linkRight <= scrollLeft + listWidth - edgeInset) {
          return;
        }

        const targetScroll = linkLeft - listWidth / 2 + linkWidth / 2;
        const maxScroll = list.scrollWidth - listWidth;
        const clampedScroll = Math.max(0, Math.min(targetScroll, maxScroll));

        if (Math.abs(scrollLeft - clampedScroll) < 2) return;

        list.scrollTo({
          left: clampedScroll,
          behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
        });
      }

      getScrollOffset() {
        const stickyTop = parseFloat(getComputedStyle(this).getPropertyValue('--bst-product-nav-sticky-top'));
        if (Number.isFinite(stickyTop) && stickyTop > 0) {
          return stickyTop;
        }

        return this.getHeaderOffset();
      }

      getAnchorScrollOffset() {
        const navHeight = this.offsetHeight || 0;
        return this.getScrollOffset() + navHeight + 16;
      }

      updateStickyOffset() {
        const offset = this.getHeaderOffset();
        const navHeight = this.offsetHeight || 0;
        const offsetValue = `${offset}px`;
        const scrollMargin = offset + navHeight + 16;

        document.documentElement.style.setProperty('--bst-header-height', offsetValue);
        document.documentElement.style.setProperty('--bst-product-nav-sticky-top', offsetValue);
        document.documentElement.style.setProperty('--bst-product-nav-scroll-margin', `${scrollMargin}px`);
        this.style.setProperty('--bst-product-nav-sticky-top', offsetValue);
      }

      getHeaderOffset() {
        const header = document.querySelector('.bst-header');
        if (header) {
          const rect = header.getBoundingClientRect();
          if (rect.height > 0) {
            return Math.ceil(rect.height);
          }
        }

        const headerBar = document.querySelector('.bst-header__bar');
        if (headerBar) {
          const barHeight = Math.ceil(headerBar.getBoundingClientRect().height);
          if (barHeight > 0) {
            return barHeight;
          }
        }

        return this.getFallbackHeaderOffset();
      }

      getFallbackHeaderOffset() {
        return window.matchMedia('(min-width: 990px)').matches ? 72 : 50;
      }
    }
  );
}
